'use client';

import { isAfter, isBefore } from 'date-fns';
import type { Route } from 'next';
import { useRouter, useSearchParams } from 'next/navigation';
import FaIcon, { faTruck } from '../../../../components/icon/FaIcon/FaIcon';
import ImageView from '../../../../components/ui/ImageView/ImageView';
import TextAlert from '../../../../components/ui/TextAlert/TextAlert';
import WChanceStaging from '../../../../components/ui/WChanceStaging/WChanceStaging';
import { Format } from '../../../../lib/dateFns';
import fontsStyles from '../../../../styles/modules/fonts.module.scss';
import utilsStyles from '../../../../styles/modules/utils.module.scss';
import { newDate } from '../../../../utils/date';
import { formatDateCommon } from '../../../../utils/formatters';
import { isVersion } from '../../../../utils/version';
import { DELIVERY_STATUS, DELIVERY_TYPE, LotteryPrizeType } from '../../constants';
import type { LotteryGoodsCollection, LotteryGoodsCollectionStagingInfo } from '../../types';
import { createStagingInfo, estimatedShippingDateText, showEstimatedShippingDate } from '../../utils';
import styles from './LotteryCollectionList.module.scss';

type Props = {
  lotteryCollectionGoodsList: LotteryGoodsCollection[];
};

function LotteryCollectionList({ lotteryCollectionGoodsList }: Props) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const orderIdFromUrl = searchParams.get('orderId');

  const clickItem = (item: LotteryGoodsCollection) => {
    // オプション入力条件をチェック
    const isPrizeOptionEnabled = item.prizeOptionFlag === 1;
    const isIncompleteOption = item.incompleteOptionFlag === true;

    // prizeOptionEndAtが期限内かチェック
    const now = newDate();
    const optionEndDate = newDate(item.prizeOptionEndAt);
    const isWithinOptionPeriod = now <= optionEndDate;

    // オプション入力ページに遷移する条件（未完了の場合）
    if (isVersion(item.version, '2.0') && isPrizeOptionEnabled && isIncompleteOption && isWithinOptionPeriod) {
      // getOptionGoodsのクエリパラメータを準備
      const queryParams = new URLSearchParams();
      queryParams.set('goodsId', item.goodsId.toString()); // lottery_prize_goods_id
      if (item.cutoffId) {
        queryParams.set('cutoffId', item.cutoffId.toString()); // lottery_cutoff_id
      }

      if (item.orderId) {
        queryParams.set('orderId', item.orderId); // order_id
      }

      const optionUrl = `/account/collection/option/input?${queryParams.toString()}`;

      router.push(optionUrl as Route);
      return;
    }

    // オプション完了済みの場合の条件チェック
    if (isVersion(item.version, '2.0') && !isIncompleteOption) {
      // オプション完了済みの場合は景品詳細ページに遷移
      router.push(`/account/collection/${item.goodsId}?cutoffId=${item.cutoffId || ''}` as Route);
      return;
    }

    if (
      item.deliveryStatus === DELIVERY_STATUS.no_address.value &&
      item.deliveryType === DELIVERY_TYPE.delivery.value
    ) {
      // 住所が必要なのに未登録の場合は履歴に飛ばせないため、結果画面に飛ばす
      router.push(`/draw/${item.lotteryDrawHistoryId}/result`);
      return;
    }

    router.push(`/account/collection/${item.goodsId}?deliveryId=${item.deliveryId || ''}` as Route);
    return;
  };

  const clickEvent = (e: React.MouseEvent, item: LotteryGoodsCollection) => {
    e.preventDefault();
    e.stopPropagation();
    router.push(`/lotteries/${item.lotteryEventId}` as Route);
    return;
  };

  const showEstimatedShippingDateCollection = (item: LotteryGoodsCollection) => {
    // カスタムオプションがあれば表示なし
    if (item.customOptions?.hideEstimatedShipmentDays === '1') return false;

    // 準備中以外は表示しない
    if (
      item.deliveryStatus === DELIVERY_STATUS.complete.value ||
      item.deliveryStatus === DELIVERY_STATUS.no_address.value ||
      item.deliveryStatus === DELIVERY_STATUS.return.value
    ) {
      return false;
    }

    // モノ以外は非表示
    if (item.prizeLabelType !== LotteryPrizeType.goods.value) return false;

    return showEstimatedShippingDate(item.lotteryEventEndAt, item.estimatedShippingDate);
  };

  const handleOptionClick = (item: LotteryGoodsCollection, path: string, orderId?: string | null) => {
    const queryParams = new URLSearchParams();
    queryParams.set('goodsId', item.goodsId.toString());
    if (item.cutoffId) {
      queryParams.set('cutoffId', item.cutoffId.toString());
    }
    if (orderId) {
      queryParams.set('orderId', orderId);
    }
    router.push(`${path}?${queryParams.toString()}` as Route);
  };

  const handleOptionInputClick = (item: LotteryGoodsCollection) => {
    handleOptionClick(item, '/account/collection/option/input', item.orderId);
  };

  const handleOptionViewClick = (item: LotteryGoodsCollection) => {
    handleOptionClick(item, '/account/collection/option', orderIdFromUrl);
  };

  const shouldShowOptionInputButton = (item: LotteryGoodsCollection): boolean => {
    if (item.prizeOptionFlag !== 1) return false;
    if (!isVersion(item.version, '2.0')) return false;
    if (!item.incompleteOptionFlag) return false;
    if (!item.prizeOptionEndAt) return false;
    if (!isAfter(newDate(item.prizeOptionEndAt), newDate())) return false;
    return true;
  };

  const shouldShowOptionViewButton = (item: LotteryGoodsCollection): boolean => {
    if (item.prizeOptionFlag === 0) return false;
    if (!isVersion(item.version, '2.0')) return false;
    if (!item.prizeOptionEndAt) return false;
    if (!isBefore(newDate(item.prizeOptionEndAt), newDate())) return false;
    return true;
  };

  return (
    <ul className={styles.lotteryCollectionList}>
      {formattedItems(lotteryCollectionGoodsList).map((item, index) => (
        <li key={`${item.goodsId}_${item.deliveryStatus}_${item.deliveryId ?? 'no-delivery'}_${index}`}>
          <div
            className={styles.itemCard}
            onClick={() => {
              clickItem(item);
            }}
            data-testid='collection-item'
          >
            <div className={styles.itemDetailWrapper}>
              <div className={styles.imageWrapper}>
                <div className={styles.itemImage}>
                  <ImageView alt='景品' src={item.imageUrl} />
                </div>
              </div>
              <div className={styles.itemDetail}>
                {/* 配送状況 */}
                <div className={styles.deliveryStatus}>
                  {item.deliveryStatus != null && (
                    <b
                      style={{
                        color: DELIVERY_STATUS[item.deliveryStatus].color,
                      }}
                    >
                      {DELIVERY_STATUS[item.deliveryStatus].text}
                    </b>
                  )}
                </div>
                {/* 演出の出しわけ（特典 | Wチャンス | それ以外） */}
                {item.customOptions?.hidePrizeLabel !== '1' ? (
                  staging(item)
                ) : (
                  <div className={styles.dummyStagingRank} />
                )}
                {/* 配送目安日 */}
                {showEstimatedShippingDateCollection(item) && (
                  <div className={styles.estimatedShippingDate}>
                    <FaIcon icon={faTruck} />
                    &nbsp;
                    {estimatedShippingDateText({
                      eventEndAt: item.lotteryEventEndAt,
                      estimatedShippingDate: item.estimatedShippingDate || undefined,
                    })}
                  </div>
                )}

                {/* 景品名 */}
                <p
                  className={`${utilsStyles.mtXSmall} ${styles.itemName} ${utilsStyles.textSmall} ${utilsStyles.textBold}`}
                >
                  {item.goodsName}
                </p>
                <p className={`${utilsStyles.textSmall} ${utilsStyles.mtXSmall}`}>保有数：{item.count}</p>
                <div className={styles.itemNameWrapper}>
                  {/* biome-ignore lint/a11y/useValidAnchor: <explanation> */}
                  <a
                    href='#'
                    className={`${utilsStyles.textSmall} ${utilsStyles.textLink} ${utilsStyles.textEllipsis} ${styles.displayBlock}`}
                    onClick={(e) => {
                      clickEvent(e, item);
                    }}
                  >
                    {item.lotteryEventName}
                  </a>
                </div>
              </div>
            </div>
            {item.prizeOptionFlag === 1 && item.incompleteOptionFlag && (
              <div
                className={`${utilsStyles.mtXSmall} ${utilsStyles.textXLarge} ${utilsStyles.textBold} ${utilsStyles.textCenter}`}
              >
                <TextAlert>
                  {/* アイコンとテキストの間に半角空白一文字の対応を入れたいため、半角空白を入れている */}
                  &nbsp;オプション入力が必要な景品があります
                  <br />
                  <span className={styles.optionEndAt}>
                    (入力期限：{formatDateCommon(item.prizeOptionEndAt, Format.dateTimeSlash)}まで)
                  </span>
                </TextAlert>
              </div>
            )}
            {shouldShowOptionInputButton(item) && (
              <div
                className={utilsStyles.mtXSmall}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <button
                  type='button'
                  className={`${styles.optionButton}`}
                  onClick={() => {
                    handleOptionInputClick(item);
                  }}
                >
                  オプションを入力する
                </button>
              </div>
            )}
            {shouldShowOptionViewButton(item) && (
              <div className={`${utilsStyles.mtSmall} `}>
                <button
                  type='button'
                  className={`${styles.actionButton} `}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOptionViewClick(item);
                  }}
                >
                  入力済みのオプションを確認する ※編集不可
                </button>
              </div>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

function formattedItems(items: Array<LotteryGoodsCollection>) {
  return items.map((item) => {
    return {
      ...item,
      ...createStagingInfo(item.stagingKey),
    };
  });
}

function getRankStyle(rank: number) {
  switch (rank) {
    case 1:
      return styles.rank1;
    case 2:
      return styles.rank2;
    case 3:
      return styles.rank3;
    default:
      return styles.defaultRankImage;
  }
}

function staging(item: LotteryGoodsCollection & LotteryGoodsCollectionStagingInfo) {
  // リポストキャンペーン
  if (item.isRepostCampaign) {
    return (
      <div className={styles.repostWrapper}>
        <span className={fontsStyles.montserrat}>リポストキャンペーン</span>
      </div>
    );
  }
  // X連特典
  if (item.benefitMultiDrawCount > 0) {
    return (
      <div className={styles.benefitWrapper}>
        <span className={fontsStyles.montserrat}>{item.benefitMultiDrawCount}</span>
        <small>連特典</small>
      </div>
    );
  }
  if (item.isWchance) {
    // Wチャンス
    return <WChanceStaging size={'default'} />;
  }
  if (item.stagingKey) {
    // 演出
    return (
      <div className={`${styles.stagingRank} ${getRankStyle(Number(item.stagingRank))} `}>
        <p className={`${styles.stagingText} ${fontsStyles.montserrat}`}>
          {item.stagingText}
          <small>賞</small>
        </p>
      </div>
    );
  }

  return <></>;
}

export default LotteryCollectionList;
